<script setup lang="ts">
    import * as yup from 'yup';
    const store = useOwnerRegisterStore();
    const { formOneData, $setFormOneData, $setFormDashboardUrlData } = store;

    const phoneRegExp = /^([1-9]{1}[0-9]{9})$/;
    const passwordRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,32}$/;

    const form = ref(null);
    
    // Date of birth state objects
    const birthYear = ref<number | null>(null);
    const birthMonth = ref<number | null>(null);
    const birthDay = ref<number | null>(null);
    
    // Create a separate ref for birthday to connect with form validation
    const birthday = ref(formOneData?.birthday || null);
    
    // Initialize birthday from formOneData if available
    onMounted(() => {
        if (formOneData && formOneData.birthday) {
            const date = new Date(formOneData.birthday);
            if (!isNaN(date.getTime())) {
                birthYear.value = date.getFullYear();
                birthMonth.value = date.getMonth() + 1;
                birthDay.value = date.getDate();
                birthday.value = date;
            }
        }
    });
    
    // Handle year change event
    const handleYearChange = (year: number) => {
        birthYear.value = year;
        updateBirthday();
    };
    
    // Handle month change event
    const handleMonthChange = (month: number) => {
        birthMonth.value = Number(month);
        updateBirthday();
    };
    
    // Handle day change event
    const handleDayChange = (day: number) => {
        birthDay.value = Number(day);
        updateBirthday();
    };
    
    // Update the birthday field when year, month, or day changes
    const updateBirthday = () => {
        if (birthYear.value && birthMonth.value && birthDay.value) {
            const birthDate = new Date(birthYear.value, birthMonth.value - 1, birthDay.value);
            if (!isNaN(birthDate.getTime())) {
                // Update the main birthday ref
                birthday.value = birthDate;
                
                // Ensure formOneData is not null before setting the birthday
                if (formOneData) {
                    formOneData.birthday = birthDate;
                }
                
                console.log("Birthday updated:", birthDate);
            }
        }
    };

    const schema = yup.object().shape({
        firstname: yup.string().required('First Name is required'),
        lastname: yup.string().required('Last Name is required'),
        birthday: yup.date().required('Date of Birth is required'),
        email: yup.string().email('Invalid email format').required('Email is required'),
        contact: yup.string().matches(phoneRegExp, "Invalid phone format").required('Phone is required').max(10),
        password: yup.string().matches(passwordRegExp, "Please enter a combination of alphabetic and numeric characters between 8 and 32").required('Password is required'),
        password_confirmation: yup.string()
            .oneOf([yup.ref('password')], 'Passwords must match')
            .required('Confirm Password is required'),
        agree: yup.boolean().oneOf([true], 'You must agree to continue').required('Agreement is required')
    })

    const { handleSubmit, resetForm, setFieldValue, errors } = useForm({
        validationSchema: schema,
        initialValues: {
            ...formOneData,
            birthday: birthday.value
        }
    });

    const onInvalidSubmit = ({ errors }: any) => {
        submitted.value = true;
        console.log('Form validation errors:', errors);
        const firstErrorFieldName = Object.keys(errors)[0];
        scrollToValidation(firstErrorFieldName);
    }

    const onSuccess = async(values: any) => {
        console.log('Form submitted successfully:', values);
        $setFormOneData(values);
        handleValidSubmitEvent();
    }

    const scrollToValidation = (errorName: any) => {
        const el = document.querySelector(`[name="${errorName}"]`);
        if (el) {
            el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    };

    watch(birthday, (newVal) => {
        if (newVal) {
            const formattedDate = newVal.toDateString();
            setFieldValue('birthday', formattedDate);
        }
    });

    const submitted = ref(false);

    const submit = handleSubmit(onSuccess, onInvalidSubmit)

    // Exposes the submit button to the parent.
    // For parent access
    defineExpose({
        submit,
    });

    const emit = defineEmits<{
        (e: 'submitEvent'): void,
    }>()

    const handleValidSubmitEvent = () => {
        emit('submitEvent') // Emit the event when the button is clicked
    }
</script>

<template>
    <form
        ref="form"
        @submit="submit">
        <Block class="mt-[2.4rem] border-[lightGray] border-[.1rem] py-[2.4rem] flex flex-col justify-center items-center rounded-[.8rem]">
            <Block class="max-w-[84rem] w-full mx-auto max-md:px-[1.6rem]">
                <TitleText
                    class="text-center"
                    type="4">
                    Customer Information
                </TitleText>
                <p class="text-[1.4rem] leading-[1.5] mt-[2.4rem]">Fields marked with <span class="text-[redEmphasis]">*</span> are required.</p>
                <Block class="mt-[2.4rem]">
                    <Block class="flex items-center flex-1 gap-[.8rem] max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="firstname">Full Name <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex w-full items-start">
                            <Block class="flex flex-wrap w-full">
                                <InputText
                                    class=" font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                    inputName="firstname"
                                    icon="none"
                                    inputType="text"
                                    placeholder="First Name"
                                />
                            </Block>
                            <Block class="flex flex-wrap w-full ml-[1rem] ">
                                <InputText
                                    class=" font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                    inputName="lastname"
                                    icon="none"
                                    inputType="text"
                                    placeholder="Last Name"
                                />
                            </Block>
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="birthday">Date of Birth <span class="text-[redEmphasis]">*</span></label>
                        <Block class="w-full grid-cols-[repeat(3,_minmax(0,_1fr))] gap-1.5 grid">
                            <DropdownDate
                                inputName="birthMonth" 
                                type="month" 
                                dateType="past" 
                                placeholder="Month" 
                                icon="calendar"
                                @changeEvent="handleMonthChange"
                                class="h-[4rem]"
                                :submitted="submitted"
                                :hasError="submitted && errors.birthday"
                            />
                            <DropdownDate
                                inputName="birthDay" 
                                type="day" 
                                dateType="past" 
                                placeholder="Day" 
                                icon="calendar"
                                @changeEvent="handleDayChange"
                                class="h-[4rem]"
                                :submitted="submitted"
                                :hasError="submitted && errors.birthday"
                            />
                            <DropdownDate
                                inputName="birthYear" 
                                type="year" 
                                dateType="past" 
                                placeholder="Year" 
                                icon="calendar"
                                @changeEvent="handleYearChange"
                                class="h-[4rem]"
                                :submitted="submitted"
                                :hasError="submitted && errors.birthday"
                            />
                            <input type="hidden" name="birthday" :value="birthday" />
                            <div v-if="submitted && errors.birthday" class="text-[red] text-[1.4rem] mt-[.4rem] block">
                                {{ errors.birthday }}
                            </div>
                        </Block>
                    </Block>
    
                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="email">Email Address <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="email"
                                icon="none"
                                inputType="email"
                                placeholder="mail@kichieats.ph"
                            />
                        </Block>
                    </Block>
    
                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="phone">Phone Number <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPhone
                                class="border-lightGray border-solid border-[1px] rounded-[1rem] overflow-hidden"
                                inputName="contact"
                                placeholder="00000000000"
                            />
                        </Block>
                    </Block>
    
                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="password">Password <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPassword
                                class="font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="password"
                                icon="none"
                                placeholder="Password"
                            />
                        </Block>
                    </Block>
    
                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem]" for="password_confirmation">Confirm Password <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPassword
                                class="font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="password_confirmation"
                                icon="none"
                                placeholder="Confirm Password"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[2.4rem] max-md:flex-col max-md:items-center">
                        <AgreeBox
                            class="flex items-center gap-0"
                            inputName="agree"
                            label="I agree to the"
                        />
                    </Block>
                </Block>
            </Block>
        </Block>
    </form>
</template>